import { useEffect, useState } from 'react';
import axios from 'axios';
import { Check, ArrowRight, Sparkles } from 'lucide-react';
import {
  PLANS,
  FOUNDING_MEMBER_ENABLED,
  FOUNDING_MEMBER_PRICE_PENCE,
  displayPrice,
  annualMonthlyEquivalent,
} from '../../config/pricing';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Feb 2026 Pilot Readiness Sprint — the one and only public pricing card
// used by LandingPage + LearnMorePage. Everything renders from the
// pricing-source-of-truth in /app/frontend/src/config/pricing.js and the
// live /api/founding-members/status counter so a "seats left" badge always
// reflects reality. Do NOT hardcode any amounts here.

const PLAN_ORDER = ['payg', 'monthly', 'annual', 'multisite'];

function PlanCard({ plan, foundingSeatsRemaining, onCtaClick }) {
  const isFoundingEligible = plan.code === 'monthly' && FOUNDING_MEMBER_ENABLED && foundingSeatsRemaining > 0;
  const isMonthly = plan.code === 'monthly';
  const isAnnual = plan.code === 'annual';
  const isMultisite = plan.code === 'multisite';
  const isPayg = plan.code === 'payg';

  return (
    <div
      className={`relative rounded-3xl p-6 sm:p-7 flex flex-col ${
        isMonthly
          ? 'bg-white border-2 border-[#DA291C] shadow-xl shadow-red-900/20'
          : 'bg-white border border-slate-200 shadow-sm'
      }`}
      data-testid={`pricing-card-${plan.code}`}
    >
      {isMonthly && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center px-3 py-1 rounded-full bg-[#DA291C] text-white text-[10.5px] font-bold uppercase tracking-[0.16em] shadow-md">
          Most popular
        </span>
      )}
      {isFoundingEligible && (
        <span
          className="absolute -top-3 right-4 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-400 text-[#7a160c] text-[10px] font-bold uppercase tracking-[0.12em] shadow"
          data-testid="founding-member-seats-badge"
        >
          <Sparkles className="h-3 w-3" /> {foundingSeatsRemaining} of 20 seats left
        </span>
      )}
      <h3 className="text-lg font-extrabold tracking-tight text-[#0B132B] mb-1" data-testid={`${plan.code}-name`}>
        {plan.name}
      </h3>
      <p className="text-[13px] text-slate-500 leading-relaxed mb-4">{plan.tagline}</p>

      <div className="flex items-baseline gap-1.5 mb-1">
        <span className="text-4xl sm:text-5xl font-extrabold text-[#0B132B] tabular-nums" data-testid={`${plan.code}-price`}>
          {displayPrice(plan.amountPence)}
        </span>
        <span className="text-sm font-medium text-slate-500">
          {isPayg ? '/shift, billed monthly' : isAnnual ? '/year' : '/month'}
        </span>
      </div>

      {isAnnual && (
        <p className="text-[12.5px] text-emerald-700 font-semibold mb-3" data-testid="annual-monthly-equivalent">
          Equivalent to {annualMonthlyEquivalent()}/month
        </p>
      )}
      {isMultisite && (
        <p className="text-[12.5px] text-slate-600 mb-3">Up to {plan.maxLocations} locations included.</p>
      )}
      {isMonthly && isFoundingEligible && (
        <p className="text-[12.5px] text-amber-700 font-semibold mb-3" data-testid="founding-member-price-line">
          Founding Member offer: {displayPrice(FOUNDING_MEMBER_PRICE_PENCE)}/month for the first 20 customers — locked in for life.
        </p>
      )}
      {isPayg && (
        <p className="text-[12.5px] text-slate-600 mb-3" data-testid="payg-billing-explainer">
          £3.99 per successfully completed shift, billed monthly. No monthly minimum. Also the default plan after your 14-day trial if you don&apos;t add a card.
        </p>
      )}

      <ul className="space-y-2 mb-6 text-[13px] text-slate-700 flex-1">
        {(plan.perks || []).map((p) => (
          <li key={p} className="flex items-start gap-2">
            <Check className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" strokeWidth={2.5} />
            <span>{p}</span>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={onCtaClick}
        className={`w-full h-11 rounded-full text-sm font-bold transition-colors inline-flex items-center justify-center gap-1.5 ${
          isMonthly
            ? 'bg-[#DA291C] text-white hover:bg-[#b91f13]'
            : 'bg-[#0B132B] text-white hover:bg-black'
        }`}
        data-testid={`${plan.code}-cta-btn`}
      >
        Start free trial <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
}

const PERKS = {
  payg: [
    'No monthly commitment',
    'Full manager dashboard access',
    'Notifications and alerts',
    'Till audit trail included',
  ],
  monthly: [
    'Unlimited shifts at your location',
    'Priority support',
    'Founding Member offer for the first 20 customers',
    'Cancel any time',
  ],
  annual: [
    'Everything in Monthly',
    'Two months free vs paying monthly',
    'Best value for a single location',
  ],
  multisite: [
    'Up to 5 locations under one account',
    'Consolidated billing',
    'Shared worker pool',
    'Priority support',
  ],
};

export default function PricingCards({ onCtaClick }) {
  const [seatsRemaining, setSeatsRemaining] = useState(20);

  useEffect(() => {
    axios.get(`${API}/founding-members/status`)
      .then((r) => setSeatsRemaining(r.data?.seatsRemaining ?? 20))
      .catch(() => setSeatsRemaining(20));
  }, []);

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5" data-testid="pricing-cards-grid">
      {PLAN_ORDER.map((code) => {
        const plan = { ...PLANS[code], perks: PERKS[code] };
        return (
          <PlanCard
            key={code}
            plan={plan}
            foundingSeatsRemaining={seatsRemaining}
            onCtaClick={onCtaClick}
          />
        );
      })}
    </div>
  );
}
